import React, { useEffect, useRef, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import dayjs, { dateDisplayFormat } from '~libs/dayjs';

import { Button, ErrorResponse, Modal, Spinner } from '~components';
import { Column, TableView } from '~components/table-view';

import { useSubmitClaimsMutation } from '~support-at-home/actions/claims';
import { parseSahApiErrors } from '~support-at-home/lib/sahApiError';
import { ClaimableClaim, SubmitClaimsResponse } from '~support-at-home/models';

interface ComponentProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  claims: ClaimableClaim[];
  onSubmitted: () => void; // parent refetches the list
}

// Reference / Period / Items - shared between the confirm-phase preview and the result-phase
// Submitted / Not included tables, all three of which list the same ClaimableClaim shape.
const CLAIM_TABLE_COLUMNS: Column[] = [
  {
    id: 'reference',
    header: 'Reference',
    type: 'text',
    classNames: { header: 'cell-align-left', column: 'cell-align-left' },
  },
  {
    id: 'period',
    header: 'Period',
    type: 'text',
    deriveValue: (row) =>
      `${dayjs(row.start_date as string).format(dateDisplayFormat)} - ${dayjs(row.end_date as string).format(dateDisplayFormat)}`,
  },
  {
    id: 'items_count',
    header: 'Items',
    type: 'text',
    deriveValue: (row) => row.items_count ?? 0,
    classNames: { header: 'cell-align-right', column: 'cell-align-right' },
  },
];

const MODAL_HEADER_ID = 'submit-claims-modal-header';

/**
 * SubmitClaimsModal (TAV-2594)
 *
 * Two phases in one modal, rather than closing on success and losing the outcome: a confirmation
 * preview of every locally-submitted, unclaimed claim, then - after the provider-wide
 * `POST /submit-claim` - a result view naming the Services Australia claim and exactly which local
 * claims it did (and did not) reconcile onto. See Claims.tsx for why this is a company-scoped
 * action rather than a per-claim one.
 */
const SubmitClaimsModal: React.FC<ComponentProps> = ({ isOpen, setIsOpen, claims, onSubmitted }) => {
  const [result, setResult] = useState<SubmitClaimsResponse | null>(null);
  const [submitClaims, { isLoading, error, reset }] = useSubmitClaimsMutation();

  // Reset to the confirm phase on the CLOSED -> OPEN transition only, so a stale result or error
  // from a previous submission is not still on screen the next time the button is pressed.
  //
  // The ref guard is load-bearing, not defensive. RTK Query memoises `reset` on
  // [dispatch, fixedCacheKey, promise, requestId], so triggering the mutation changes its identity.
  // Without the guard, that identity change re-runs this effect WHILE the request is in flight and
  // calls reset(), tearing down the pending state: isLoading drops to false (the spinner flashes
  // and vanishes) and the error/result are discarded before they can ever render.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setResult(null);
      reset();
    }
    wasOpen.current = isOpen;
  }, [isOpen, reset]);

  const handleCancel = () => setIsOpen(false);

  const handleSubmit = async () => {
    try {
      const payload = await submitClaims().unwrap();
      setResult(payload);
    } catch {
      // Nothing further to do - the mutation's own error state is read below and rendered inside
      // the modal, next to the list the user was confirming.
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onSubmitted();
  };

  // Errors render INSIDE the modal, next to the list the user was confirming - not via a toast that
  // vanishes, and never as the raw string. The API deliberately keeps the full vendor JSON in
  // `message` for diagnosability; `parseSahApiErrors` reduces it to the human-readable part(s).
  //
  // Plural, one message per line: Services Australia can return several distinct failures in
  // `details[]`, and running them together is what makes a multi-failure error unreadable (see the
  // guidance in sahApiError.ts). The single-line `parseSahApiError` exists for toasts only.
  const errorMessages = error ? parseSahApiErrors(getErrorMessage(error)) : [];

  // Not every failure means the claim was NOT lodged, and the difference matters more here than
  // almost anywhere else in the app: a Services Australia claim cannot be un-claimed, so telling a
  // provider "nothing has changed" when the request may in fact have landed invites a second
  // lodgement of a government payment.
  //
  // A 4xx is Services Australia (via our API) REPORTING a rejection - the request arrived, was
  // assessed, and was refused, so nothing was created. Anything else - a 5xx from our API or the
  // IBM gateway, or a non-numeric RTK status (FETCH_ERROR / TIMEOUT_ERROR / PARSING_ERROR) - means
  // the round trip never completed and the outcome is genuinely unknown to us.
  const errorStatus = (error as { status?: unknown } | undefined)?.status;
  const isReportedRejection = typeof errorStatus === 'number' && errorStatus >= 400 && errorStatus < 500;

  const claimsNotIncluded = result?.claims_not_included ?? [];

  // An UNCONFIRMED failure ends the interaction, exactly as a result does - it does not offer the
  // action again. The body above tells the user to close and reload before trying anything, and on
  // the api's saAccepted path the message is literally "Do not retry - contact support"; leaving a
  // green Submit Claims button directly beneath that is the app contradicting itself on the one
  // screen where an irreversible government payment may already have landed.
  //
  // A REPORTED rejection is deliberately excluded: there the request was assessed and refused, so
  // nothing was created and retrying (once the cause clears) is genuinely the right next action.
  //
  // This also decides the exit route, not just the footer - both doors have to agree. handleClose
  // refetches the list on the way out, which handleCancel does not, and after an unconfirmed failure
  // the list is exactly what the user needs re-read: the claim may in fact have been created.
  const isUnconfirmedFailure = errorMessages.length > 0 && !isReportedRejection;
  const isFinished = Boolean(result) || isUnconfirmedFailure;

  // Three exclusive phases - waiting, result, confirm - as early returns rather than a nested
  // ternary in the JSX (the repo lints `no-nested-ternary`).
  const renderBody = () => {
    if (isLoading) {
      // The vendor round-trip is TWO calls - POST the claim, then read it back to learn which
      // invoices Services Australia actually included - so this can sit for a while. Show the wait
      // explicitly rather than leaving the confirm table on screen looking inert; the confirm
      // button's own spinner is too small to carry a multi-second government API call.
      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center py-5"
          data-cy="submit-claims-modal-loading"
          role="status"
          aria-live="polite"
        >
          <Spinner loading />
          <p className="mt-3 mb-0 text-center">Submitting to Services Australia. This can take a moment.</p>
          <p className="mt-1 mb-0 text-center fst-italic">Do not close this window.</p>
        </div>
      );
    }

    if (result) {
      return (
        <div data-cy="submit-claims-modal-result">
          <p className="mb-3" data-cy="submit-claims-modal-result-success">
            Services Australia claim {result.sa_claim_id} created.
          </p>
          {/*
            The caveat leads the tables rather than trailing them. It qualifies BOTH lists below, so
            reading it afterwards is reading it too late - and on the result screen of an
            irreversible payment, "this is unconfirmed" is not a footnote.
          */}
          {result.reconciled_from === 'response' && (
            <p className="mb-3 fst-italic" data-cy="submit-claims-modal-reconciled-from-response">
              Services Australia has not yet confirmed which invoices are in this claim. The lists below are
              TurnPoint&apos;s record and will be reconciled when the claim is next checked.
            </p>
          )}
          <h4 className="h6 fw-semibold mb-2">Submitted ({result.claims_submitted.length})</h4>
          <TableView
            id="submit-claims-result-table"
            columns={CLAIM_TABLE_COLUMNS}
            data={result.claims_submitted}
            readonly
            striped
            emptyMessage="No claims were submitted"
          />
          {claimsNotIncluded.length > 0 && (
            <div className="mt-4" data-cy="submit-claims-modal-not-included">
              <h4 className="h6 fw-semibold mb-2 text-warning">Not included ({claimsNotIncluded.length})</h4>
              <p className="mb-2">
                Services Australia did not include these in the claim. They remain Submitted and will be picked up
                by the next claim.
              </p>
              <TableView
                id="submit-claims-not-included-table"
                columns={CLAIM_TABLE_COLUMNS}
                data={claimsNotIncluded}
                readonly
                striped
                emptyMessage="No claims were excluded"
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div data-cy="submit-claims-modal-confirm">
        {/*
          A rejection is an OUTCOME, so it leads - it does not sit under the table the user just
          confirmed, where it reads as a footnote to a screen that otherwise looks unchanged. The
          claim list stays below it either way: on a REPORTED rejection so the same action can be
          retried once the cause is cleared (e.g. Services Australia finishes processing the claim
          already in progress), and on an unconfirmed failure as the record of which claims may have
          been swept in - there the footer is Close-only, so it is reference, not an invitation.
        */}
        {errorMessages.length > 0 && (
          <div className="mb-4" data-cy="submit-claims-modal-error" role="alert">
            <h4 className="h6 fw-semibold text-danger mb-2">
              {isReportedRejection
                ? 'Services Australia did not create the claim'
                : 'TurnPoint could not confirm the outcome'}
            </h4>
            {errorMessages.map((message) => (
              <ErrorResponse key={message} message={message} className="justify-content-start m-0 mb-1" />
            ))}
            {/*
              Only a REPORTED rejection earns the categorical reassurance. On an unconfirmed failure
              the honest answer is that we do not know, and the user is pointed at a reload rather
              than straight back at the Submit Claims button still sitting below.
            */}
            {isReportedRejection ? (
              <p className="mt-2 mb-0 fst-italic" data-cy="submit-claims-modal-error-rejected">
                No claims were submitted. Nothing has changed.
              </p>
            ) : (
              <p className="mt-2 mb-0 fst-italic" data-cy="submit-claims-modal-error-unconfirmed">
                The claim may or may not have reached Services Australia. Close this window and reload the claims
                list to check before trying again.
              </p>
            )}
          </div>
        )}
        {/*
          The lede has to follow the footer. Once the footer is Close-only the action is over, and
          "You are about to submit" describes something the user can no longer do from this screen -
          worse, it reads as an instruction to press a button that is no longer there.
        */}
        {isUnconfirmedFailure ? (
          <p className="mb-3" data-cy="submit-claims-modal-lede-unconfirmed">
            These are the {claims.length} claims that may have been included:
          </p>
        ) : (
          <p className="mb-3" data-cy="submit-claims-modal-lede">
            You are about to submit {claims.length} claims to Services Australia. They will be combined into a single
            claim for payment.
          </p>
        )}
        <TableView
          id="submit-claims-table"
          columns={CLAIM_TABLE_COLUMNS}
          data={claims}
          readonly
          striped
          emptyMessage="No claims are ready to submit"
        />
        <p className="mt-3 mb-0 fst-italic" data-cy="submit-claims-modal-footer-note">
          Once submitted, these claims cannot be edited or re-opened.
        </p>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      // NOT the raw setIsOpen. Modal renders <ModalHeader toggle={() => setIsOpen(!isOpen)}>, so the
      // header's ✕ bypasses handleCancel/handleClose entirely - and unlike the footer Cancel button
      // it would not be disabled mid-submit. That let a user dismiss an in-flight claim, reopen from
      // the still-enabled toolbar button (Claims.tsx cannot see the pending mutation - it lives in
      // here), re-fire the open transition so the wasOpen effect called reset() on the live request,
      // and confirm a SECOND POST. Duplicate claim, no un-claim operation. Routing ✕ through the
      // same phase-aware handlers closes it. Precedent: DashboardTableModal.tsx:151-153.
      setIsOpen={(open) => {
        if (open || isLoading) return;
        if (isFinished) {
          handleClose();
        } else {
          handleCancel();
        }
      }}
      size="lg"
      header={<span id={MODAL_HEADER_ID}>Submit claims to Services Australia</span>}
      labelledBy={MODAL_HEADER_ID}
      includeFooter={true}
      data-cy="submit-claims-modal"
      footer={
        isFinished ? (
          <Button
            size="sm"
            className="ps-4 pe-4"
            color="success"
            onClick={handleClose}
            dataCy="submit-claims-modal-close-button"
          >
            Close
          </Button>
        ) : (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              className="ps-4 pe-4"
              color="success"
              loading={isLoading}
              // `loading` replaces the button's children with a spinner, losing its accessible name.
              aria-label="Submit Claims"
              onClick={handleSubmit}
              dataCy="submit-claims-modal-confirm-button"
            >
              Submit Claims
            </Button>
            <Button
              size="sm"
              className="ps-4 pe-4"
              onClick={handleCancel}
              disabled={isLoading}
              dataCy="submit-claims-modal-cancel-button"
            >
              Cancel
            </Button>
          </div>
        )
      }
    >
      {renderBody()}
    </Modal>
  );
};

export default SubmitClaimsModal;
